import { Localized } from "@fluent/react/compat";
import React, { FunctionComponent, useCallback } from "react";
import { Field } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";

import SiteSearchDropdown from "coral-admin/components/SiteSearchDropdown/SiteSearchDropdown";
import { SITE_BADGE_OPTIONS } from "coral-admin/constants/badges";
import {
  Box,
  Button,
  ButtonIcon,
  Flex,
  FormField,
  FormFieldDescription,
  FormFieldHeader,
  HorizontalGutter,
  Label,
  Option,
  SelectField,
  TextField,
} from "coral-ui/components/v2";

import Subheader from "../../Subheader";

import styles from "./BadgeItem.css";

interface Props {
  disabled: boolean;
}

const BadgesByDateSection: FunctionComponent<Props> = ({ disabled }) => {
  const onAddBadge = useCallback((push: any) => {
    push({
      siteID: "",
      badgeName: "",
      startDate: "",
      endDate: "",
    });
  }, []);

  const onRemoveBadge = useCallback((remove: any, index: number) => {
    remove(index);
  }, []);

  return (
    <HorizontalGutter spacing={4}>
      <Localized id="configure-badges-byDate-description">
        <FormFieldDescription>
          Configure badges for users on specific sites based on date ranges.
          Each badge will be applied to all users on the selected site during
          the specified date range. You can add multiple badges with different
          date ranges.
        </FormFieldDescription>
      </Localized>

      <FieldArray name="badgesByDate">
        {({ fields }) => (
          <>
            <Button
              iconLeft
              onClick={() => onAddBadge(fields.push)}
              disabled={disabled}
            >
              <ButtonIcon size="md">add</ButtonIcon>
              <Localized id="configure-badges-addBadge">Add Badge</Localized>
            </Button>

            {fields.map((badge: string, index: number) => (
              <Box key={index}>
                <Field name={`${badge}.badgeName`}>
                  {({ input }) => (
                    <Subheader>
                      <Flex justifyContent="space-between">
                        <div>
                          {input.value ? (
                            SITE_BADGE_OPTIONS.find(
                              (opt) => opt.value === input.value
                            )?.label || input.value
                          ) : (
                            <Localized id="configure-badges-defaultName">
                              New Badge
                            </Localized>
                          )}
                        </div>
                      </Flex>
                    </Subheader>
                  )}
                </Field>

                <Box className={styles.content}>
                  <HorizontalGutter spacing={3}>
                    <FormField>
                      <Field name={`${badge}.siteID`}>
                        {({ input }) => (
                          <>
                            <SiteSearchDropdown
                              value={input.value}
                              onChange={input.onChange}
                              disabled={disabled}
                              placeholder="Select site"
                            />
                          </>
                        )}
                      </Field>
                    </FormField>

                    <FormField>
                      <FormFieldHeader>
                        <Localized id="configure-badge-name">
                          <Label>Badge Name</Label>
                        </Localized>
                      </FormFieldHeader>
                      <Field name={`${badge}.badgeName`}>
                        {({ input }) => (
                          <SelectField
                            id={`${badge}.badgeName`}
                            {...input}
                            disabled={disabled}
                            fullWidth
                          >
                            <Option value="">Select a badge type</Option>
                            {SITE_BADGE_OPTIONS.map((option) => (
                              <Option key={option.value} value={option.value}>
                                {option.label}
                              </Option>
                            ))}
                          </SelectField>
                        )}
                      </Field>
                    </FormField>

                    <FormField>
                      <FormFieldHeader>
                        <Localized id="configure-badge-startDate">
                          <Label>Start Date</Label>
                        </Localized>
                      </FormFieldHeader>
                      <Field name={`${badge}.startDate`}>
                        {({ input }) => (
                          <TextField
                            {...input}
                            type="date"
                            fullWidth
                            disabled={disabled}
                          />
                        )}
                      </Field>
                    </FormField>

                    <FormField>
                      <FormFieldHeader>
                        <Localized id="configure-badge-endDate">
                          <Label>End Date</Label>
                        </Localized>
                      </FormFieldHeader>
                      <Field name={`${badge}.endDate`}>
                        {({ input }) => (
                          <TextField
                            {...input}
                            type="date"
                            fullWidth
                            disabled={disabled}
                          />
                        )}
                      </Field>
                    </FormField>
                  </HorizontalGutter>

                  <div className={styles.removeButton}>
                    <Button
                      color="alert"
                      onClick={() => onRemoveBadge(fields.remove, index)}
                    >
                      <Localized id="configure-badges-remove">
                        Remove Badge
                      </Localized>
                    </Button>
                  </div>
                </Box>
              </Box>
            ))}
          </>
        )}
      </FieldArray>
    </HorizontalGutter>
  );
};

export default BadgesByDateSection;
